/**
 * What a learner's picture draws as, and the crop maths behind an upload
 * (learner picture PRD §3.1, §7.2). Pure, so the `.tsx` that renders it and
 * the dialog that crops stay thin.
 */
import { isPresetSlug, type PresetSlug } from './presets.ts';

export type PictureView =
  | { kind: 'preset'; slug: PresetSlug }
  | { kind: 'photo'; url: string }
  | { kind: 'initials'; grapheme: string; colour: string };

/**
 * A slug this build does not know falls through to initials rather than an
 * empty circle (PRD §4): the server's list may be ahead of a cached bundle.
 */
export function pictureView(
  picture: { preset?: string | null; url?: string | null } | null | undefined,
  displayName: string,
  learnerId: string,
): PictureView {
  if (picture?.preset && isPresetSlug(picture.preset)) return { kind: 'preset', slug: picture.preset };
  if (picture?.url) return { kind: 'photo', url: picture.url };
  return initialsView(displayName, learnerId);
}

/** For the photo's `onError`: a file that 404s draws initials, not a broken image. */
export function afterImageError(_view: PictureView, displayName: string, learnerId: string): PictureView {
  return initialsView(displayName, learnerId);
}

function initialsView(displayName: string, learnerId: string): PictureView {
  return { kind: 'initials', grapheme: initialOf(displayName), colour: colourFor(learnerId) };
}

const graphemes = new Intl.Segmenter(undefined, { granularity: 'grapheme' });

/**
 * A grapheme, not a code unit (PRD §3.1): a decomposed Vietnamese name opens
 * on a base letter and its combining marks, and an emoji can be a ZWJ
 * sequence of several. `'?'` rather than an empty circle for a blank name.
 */
export function initialOf(displayName: string): string {
  const first = graphemes.segment(displayName.trim())[Symbol.iterator]().next();
  return first.done ? '?' : first.value.segment.toLocaleUpperCase();
}

/**
 * Tailwind's 700 shades, each at least 4.5:1 against white text, which is
 * what the initial is drawn in on either register (PRD §3.1). The test
 * computes the ratios rather than trusting this comment.
 */
export const INITIALS_PALETTE: readonly string[] = [
  '#b91c1c',
  '#c2410c',
  '#a16207',
  '#15803d',
  '#0f766e',
  '#1d4ed8',
  '#6d28d9',
  '#be185d',
];

/**
 * FNV-1a over the global id: stable across visits and builds, so the circle
 * keeps its colour, with no dependency for a hash this small.
 */
export function colourFor(learnerId: string): string {
  let hash = 0x811c9dc5;
  for (let i = 0; i < learnerId.length; i++) {
    hash ^= learnerId.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return INITIALS_PALETTE[hash % INITIALS_PALETTE.length]!;
}

/** Centre in source pixels; `scale` is output pixels per source pixel. */
export type Crop = { cx: number; cy: number; scale: number };

/** The 512 px canvas PRD §7.1 uploads. */
const OUT = 512;

/** The zoom at which the image's short side just spans the output square. */
export function minScale(w: number, h: number, out = OUT): number {
  return out / Math.min(w, h);
}

/**
 * The square never leaves a gap (PRD §3.2): zoom no lower than `minScale`,
 * and the centre no nearer an edge than half the square's side. An image
 * smaller than the output is upscaled rather than refused.
 */
export function clampCrop(crop: Crop, w: number, h: number, out = OUT): Crop {
  const scale = Math.max(crop.scale, minScale(w, h, out));
  const half = out / scale / 2;
  return {
    cx: Math.min(Math.max(crop.cx, half), w - half),
    cy: Math.min(Math.max(crop.cy, half), h - half),
    scale,
  };
}

/** The source rectangle `drawImage` reads for a crop. */
export function sourceRect(crop: Crop, out = OUT): { sx: number; sy: number; sw: number; sh: number } {
  const side = out / crop.scale;
  return { sx: crop.cx - side / 2, sy: crop.cy - side / 2, sw: side, sh: side };
}
